import React, { useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { FileTypeIcon } from '@components/FileTypeIcon';
import documentService, { flattenFolders } from '@services/documentService';
import type { Document, DocumentFolder, DocumentInput } from '@/types/document';
import { formatBytes } from '@/utils/formatters';

interface Props {
  document: Document;
  folders: DocumentFolder[];
  onClose: () => void;
  onSaved: (doc: Document) => void;
  onError: (err: unknown) => void;
}

const labelCls = 'text-label block mb-1';

/** Edita os metadados de um documento (PUT /documents/:id). O arquivo não muda: exclua e envie de novo. */
export const DocumentEditModal: React.FC<Props> = ({ document: doc, folders, onClose, onSaved, onError }) => {
  const [form, setForm] = useState({
    title: doc.title,
    description: doc.description ?? '',
    folder_id: doc.folder_id,
    doc_date: doc.doc_date ?? '',
    expires_at: doc.expires_at ?? '',
    remind_days: doc.remind_days ?? 30,
    tags: doc.tags ?? '',
  });
  const [saving, setSaving] = useState(false);
  const flat = useMemo(() => flattenFolders(folders), [folders]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));

  const dateError = form.expires_at && form.doc_date && form.expires_at < form.doc_date ? 'A validade não pode ser antes da data do documento.' : null;
  const canSubmit = form.title.trim().length > 0 && !!form.folder_id && !dateError && !saving;

  const save = async () => {
    if (!canSubmit) return;
    const payload: DocumentInput = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      folder_id: Number(form.folder_id),
      doc_date: form.doc_date || null,
      expires_at: form.expires_at || null,
      remind_days: form.remind_days,
      tags: form.tags.trim() || null,
      ref_type: doc.ref_type ?? null,
      ref_id: doc.ref_id ?? null,
    };
    setSaving(true);
    try {
      onSaved(await documentService.update(doc.id, payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Pencil size={18} className="text-slate-500" />
        <h2 className="text-h3">Editar documento</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-4">
        <div className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
          <FileTypeIcon fileName={doc.file_name} contentType={doc.content_type} />
          <div className="min-w-0">
            <p className="text-sm text-slate-800 [overflow-wrap:anywhere]">{doc.file_name}</p>
            <p className="text-small">{formatBytes(doc.size_bytes)} · o arquivo não é alterado aqui (para trocar, exclua e envie de novo)</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Título <span className="text-red-500">*</span>
            </label>
            <input className="input-field" maxLength={200} value={form.title} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Pasta <span className="text-red-500">*</span>
            </label>
            <select className="select-field" value={form.folder_id} onChange={(e) => set('folder_id', Number(e.target.value))}>
              {flat.map(({ folder, depth }) => (
                <option key={folder.id} value={folder.id}>
                  {`${'   '.repeat(depth)}${depth > 0 ? '↳ ' : ''}${folder.name}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Data do documento</label>
            <DateInputBR className="input-field w-full" value={form.doc_date} onChange={(v) => set('doc_date', v)} />
          </div>
          <div>
            <label className={labelCls}>Validade</label>
            <DateInputBR className="input-field w-full" value={form.expires_at} onChange={(v) => set('expires_at', v)} placeholder="sem validade" />
          </div>
          <div>
            <label className={labelCls}>Avisar com antecedência (dias)</label>
            <input
              type="number"
              min={0}
              max={365}
              className="input-field"
              value={form.remind_days}
              onChange={(e) => set('remind_days', Math.min(365, Math.max(0, Number(e.target.value) || 0)))}
            />
          </div>
          <div>
            <label className={labelCls}>Tags</label>
            <input className="input-field" maxLength={300} value={form.tags} placeholder="separe por vírgula" onChange={(e) => set('tags', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Descrição</label>
            <textarea className="textarea-field" rows={3} maxLength={2000} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          {doc.ref_type && (
            <p className="sm:col-span-2 text-small">
              Vinculado a {doc.ref_type === 'firearm' ? 'uma arma' : 'um documento CAC'} do Acervo (o vínculo é mantido).
            </p>
          )}
          {dateError && <p className="sm:col-span-2 text-sm text-red-600">{dateError}</p>}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
};
